import { useEffect, useState } from "react";
import useValidations from "hooks/useValidations";
import {
  Button,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import DatePickerField from "components/UI/Fields/DatePickerField";
import MaskedField from "components/UI/Fields/MaskedField";
import LabelField from "components/shared/Contract/LabelField";
import {
  ContractIntegrationStore as Integration,
  ContractReajustmentStore,
  ContractStore,
  MessageStore,
} from "store";
import { formatNumber, formatNumberToMonetary, inputTextToDate } from "utils";
import { BetweenValuesException } from "api/exceptions/InvalidValueException";
import { lang } from "i18nLang";

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 700,
    marginBottom: "0.5rem",
    color: "#AFCBFF",
  },
  button: { width: "48%" },
  input: {
    "& label": { color: "#BDBDBD !important", textTransform: "uppercase" },
    "& input": { color: "#FFFFFF" },
    "& .MuiIconButton-label, & .MuiSelect-icon": { color: "#BDBDBD" },
    "& p": { color: "#BDBDBD !important", fontSize: "0.5rem" },
    "& > div::after": { transform: "scaleX(1)" },
  },
  headerContent: { padding: "0.5rem", height: "fit-content" },
  content: { padding: "0.5rem", backgroundColor: "#212121", height: "100%" },
}));

const defaultData = {
  indicator: -1,
  readjustimentDate: new Date(),
  readjustimentPercentual: 0,
  contractTotal: 0,
  contractBalance: 0,
};

const MenuReadjustment = () => {
  const classes = useStyles();
  const contract = ContractStore.getCurrentContract();
  const [data, setData] = useState(defaultData);
  const { validated, validateFields, hasErrors } = useValidations({
    indicator: "required",
    readjustimentDate: {
      date: true,
      custom: (value) => {
        const date = value instanceof Date ? value : inputTextToDate(value);
        if (date && contract) {
          if (date.getTime() <= contract.date.getTime()) {
            return {
              valid: false,
              text: "A data de reajuste não pode ser menor que a data do Contrato",
            };
          }

          const now = new Date();
          if (date.getTime() <= now.getTime()) {
            return;
          }
        }

        return {
          valid: false,
          text: "A data de reajuste não pode ser maior que a data atual",
        };
      },
    },
    readjustimentPercentual: (value) => {
      if (!value || (value >= 0 && value <= 100)) return;

      const error = new BetweenValuesException(value, 0, 100);
      return {
        valid: false,
        text: error.customMessage,
      };
    },
  });

  // Obtém a cotação inicial ao criar componente
  useEffect(() => {
    ContractReajustmentStore.fetchAndSetPreviousQuotations().then((quota) => {
      if (quota) {
        const result = ContractReajustmentStore.calculateNewContractTotals();
        setData({
          ...data,
          ...result,
          indicator: quota.id,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualiza as cotações ao trocar de indicador
  useEffect(() => {
    if (data.indicator === -1) changeIndicatorQuotationManual(0, new Date());
    else fetchIndicatorQuotation(data.indicator, new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.indicator]);

  const handleClickCancel = () => {
    ContractStore.closeLeftSideMenu();
  };

  const handleClickApply = () => {
    if (!validateFields(data, true)) return;

    MessageStore.toggleLoading();
    ContractReajustmentStore.readjustContractTotals(
      data.readjustimentPercentual
    )
      .then(() => {
        MessageStore.success(lang("contractReadjustment.messages.success"));
        handleClickCancel();
      })
      .finally(() => {
        MessageStore.toggleLoading();
      });
  };

  const handleInputChange = (ev) => {
    const { name, value } = ev.target;
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  const handleInputDateChange = (date: Date) => {
    const newData = { ...data, readjustimentDate: date };
    setData(newData);
    fetchIndicatorQuotation(newData.indicator, newData.readjustimentDate);
  };

  const handleInputPercentualChange = (ev) => {
    const { value } = ev.target;
    changeIndicatorQuotationManual(value, data.readjustimentDate);
  };

  const fetchIndicatorQuotation = (indicatorId: number, date: Date) => {
    if (indicatorId < 0) return;

    ContractReajustmentStore.fetchAndSetPreviousQuotations(indicatorId)
      .then(() =>
        ContractReajustmentStore.fetchAndSetNewQuotationsWithERPIndicator(
          indicatorId,
          date
        )
      )
      .then((quota) => {
        if (quota) {
          const result = ContractReajustmentStore.calculateNewContractTotals();
          setData({ ...data, ...result, readjustimentDate: quota.date });
        }
      });
  };

  const changeIndicatorQuotationManual = (percentual: number, date: Date) => {
    ContractReajustmentStore.setNewQuotationsWithManualIndicator(
      percentual,
      data.readjustimentDate
    );

    const { contractTotal, contractBalance } =
      ContractReajustmentStore.calculateNewContractTotals();

    setData({
      ...data,
      readjustimentPercentual: percentual,
      readjustimentDate: date,
      contractTotal,
      contractBalance,
    });
  };

  return (
    <>
      <Grid container item className={classes.headerContent}>
        <Typography variant="body2" className={classes.title}>
          {lang("contractReadjustment.title")}
        </Typography>
        <Grid container item justifyContent="space-between">
          <Button
            variant="outlined"
            color="secondary"
            className={classes.button}
            onClick={handleClickCancel}
          >
            {lang("cancel")}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.button}
            onClick={handleClickApply}
            disabled={hasErrors()}
          >
            {lang("apply")}
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        item
        className={classes.content}
        justifyContent="flex-start"
        alignContent="flex-start"
      >
        <LabelField
          caption={lang("fields.contractDate")}
          value={contract?.date.toLocaleDateString("pt-BR") || "-"}
        />
        <TextField
          select
          name="indicator"
          label={lang("fields.indicator")}
          className={classes.input}
          value={data.indicator}
          onChange={handleInputChange}
          onBlur={(ev) => validateFields({ indicator: ev.target.value })}
          color="secondary"
          fullWidth
          InputProps={{ style: { color: "#FFFFFF" } }}
          margin="normal"
          error={!validated.indicator.valid}
          helperText={validated.indicator.text}
        >
          <MenuItem value={-1}>{lang("contractReadjustment.manual")}</MenuItem>
          {Integration.financialIndicators.map((ind) => (
            <MenuItem key={ind.id} value={ind.id}>
              {ind.name}
            </MenuItem>
          ))}
        </TextField>
        <DatePickerField
          name="readjustimentDate"
          label={lang("fields.readjustimentDate")}
          className={classes.input}
          value={data.readjustimentDate}
          onChange={handleInputDateChange}
          onBlur={(name, newDate) => validateFields({ [name]: newDate })}
          color="secondary"
          InputProps={{ style: { color: "#FFFFFF" } }}
          margin="normal"
          fullWidth
          disableFuture
          minDate={
            contract &&
            new Date(contract.date).setDate(contract.date.getDate() + 1)
          }
          error={!validated.readjustimentDate.valid}
          helperText={validated.readjustimentDate.text}
        />
        {data.indicator === -1 ? (
          <MaskedField
            name="readjustimentPercentual"
            label={lang("fields.readjustimentPercentual")}
            className={classes.input}
            suffix="%"
            decimalScale={2}
            allowNegative={false}
            value={data.readjustimentPercentual}
            onChange={handleInputPercentualChange}
            onBlur={(ev, value) => {
              validateFields({ readjustimentPercentual: value });
            }}
            onKeyDown={(ev) => ev.stopPropagation()}
            InputProps={{ style: { color: "#FFFFFF" } }}
            margin="normal"
            fullWidth
            error={!validated.readjustimentPercentual.valid}
            helperText={validated.readjustimentPercentual.text}
          />
        ) : (
          <LabelField
            caption={lang("fields.readjustimentPercentual")}
            value={
              data.readjustimentPercentual
                ? `${formatNumber(data.readjustimentPercentual)}%`
                : "-"
            }
          />
        )}
        <LabelField
          caption={lang("contractReadjustment.contractTotal")}
          value={formatNumberToMonetary(data.contractTotal)}
        />
        <LabelField
          caption={lang("contractReadjustment.contractBalance")}
          value={formatNumberToMonetary(data.contractBalance)}
        />
      </Grid>
    </>
  );
};

export default MenuReadjustment;
